import React from 'react'
import { Link } from 'react-router-dom'
import {
  COLUMN_TYPES,
  COLUMN_SORT_SETTING,
  DATA_TYPES,
  SEARCH_TYPES,
  Icon,
  reducers,
  utils,
  coreConstants,
} from '@adc/ui-morpheus'
import { VENUES } from '../../constants/tableTypes'
import {
  venueType as venueTypeList,
  venueClassification as venueClassificationList,
  venueSetting as venueSettingList,
  trusteeSalesChannel as trusteeSalesChannelList,
} from '../../constants/optionLists'

const {
  date: { API_LOCAL_DATE_FORMAT, UI_DATE_ALTERNATE_FORMAT },
} = utils
const { states: statesList, yesNo: yesNoList } = coreConstants.optionLists
const defaultColPropsWithWidth300 = {
  width: 300,
  isResizable: true,
  isReorderable: true,
  isVisible: false,
}
const defaultColPropsWithWidth100 = {
  width: 100,
  isResizable: true,
  isReorderable: true,
  isVisible: false,
}

export const tableConfig = {
  config: {
    entityKey: VENUES,
    primaryKeyField: 'venue_id',
    allowExportCSV: true,
    enableSavedSearches: true,
  },
  fieldDefinitions: [
    {
      name: 'Selected',
      apiRef: 'selected', // not api val
      isUiOnly: true,
      columnDefinition: {
        type: COLUMN_TYPES.CHECKBOX,
        colProps: { width: 40, fixed: true },
      },
    },
    {
      name: 'Venue Code',
      apiRef: 'venue_code',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: true,
          searchable: true,
        },
      },
    },
    {
      name: 'Event Code',
      apiRef: 'event.event_code',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        partialMatch: true,
        colProps: {
          ...defaultColPropsWithWidth300,
          isVisible: true,
          searchable: true,
        },
      },
    },
    {
      name: 'Name',
      apiRef: 'venue_name',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        partialMatch: true,
        colProps: {
          ...defaultColPropsWithWidth300,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.SORTED,
          searchable: true,
          sortAscending: true,
        },
      },
    },
    {
      name: 'Template Name',
      apiRef: 'venue_template_name',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        partialMatch: true,
        colProps: {
          ...defaultColPropsWithWidth300,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Title',
      apiRef: 'venue_title',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        partialMatch: true,
        colProps: {
          ...defaultColPropsWithWidth300,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Type',
      apiRef: 'venue_type',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: venueTypeList,
      columnDefinition: {
        type: COLUMN_TYPES.VALUE_TEXT,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Updated Date',
      apiRef: 'update_date',
      dataType: DATA_TYPES.DATETIME_AS_LOCAL,
      searchType: SEARCH_TYPES.DATE,
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Listing Count',
      apiRef: 'listing_count',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      name: 'Reserve Sum',
      apiRef: 'reserve_sum', // TODO api not ready
      isUiOnly: true,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      name: 'Campaign Date',
      apiRef: 'event.event_schedule.campaign_start_date',
      dataType: DATA_TYPES.DATE,
      searchType: SEARCH_TYPES.DATE,
      formatOptions: {
        dateFormat: 'datePatternPST',
      },
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: {
          ...defaultColPropsWithWidth100,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Registration Time',
      apiRef: 'venue_schedule.registration_time',
      searchType: SEARCH_TYPES.TIME,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: {
          ...defaultColPropsWithWidth100,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Start Date',
      apiRef: 'venue_schedule.bid_start_local_date',
      dataType: DATA_TYPES.DATE_AS_ISO,
      searchType: SEARCH_TYPES.DATE,
      formatOptions: {
        dateFormat: 'datePattern',
        pattern: API_LOCAL_DATE_FORMAT,
        displayFormat: UI_DATE_ALTERNATE_FORMAT,
      },
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Start Time',
      apiRef: 'venue_schedule.bid_start_local_time',
      dataType: DATA_TYPES.TIME,
      searchType: SEARCH_TYPES.TIME,
      columnDefinition: {
        type: COLUMN_TYPES.TIME,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'End Date',
      apiRef: 'venue_schedule.initial_bidding_end_local_date',
      dataType: DATA_TYPES.DATE_AS_ISO,
      searchType: SEARCH_TYPES.DATE,
      formatOptions: {
        dateFormat: 'datePattern',
        pattern: API_LOCAL_DATE_FORMAT,
        displayFormat: UI_DATE_ALTERNATE_FORMAT,
      },
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'End Time',
      apiRef: 'venue_schedule.initial_bidding_end_local_time',
      dataType: DATA_TYPES.TIME,
      searchType: SEARCH_TYPES.TIME,
      columnDefinition: {
        type: COLUMN_TYPES.TIME,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: true,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      apiRef: 'venue_configuration.is_minimum_bid_enabled',
      name: 'Minimum Bid Enabled',
      dataType: DATA_TYPES.BOOLEAN,
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: yesNoList,
      columnDefinition: {
        type: COLUMN_TYPES.BOOL,
        colProps: {
          ...defaultColPropsWithWidth100,
          searchable: true,
        },
      },
    },
    {
      name: 'Venue Classification',
      apiRef: 'venue_configuration.venue_classification',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: venueClassificationList,
      formatOptions: {
        titleCase: true,
      },
      columnDefinition: {
        type: COLUMN_TYPES.VALUE_TEXT,
        colProps: {
          ...defaultColPropsWithWidth100,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Venue Setting',
      apiRef: 'venue_configuration.venue_setting',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: venueSettingList,
      columnDefinition: {
        type: COLUMN_TYPES.VALUE_TEXT,
        colProps: {
          ...defaultColPropsWithWidth100,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Trustee Sales Channel',
      apiRef: 'venue_configuration.trustee_sales_channel',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: trusteeSalesChannelList,
      columnDefinition: {
        type: COLUMN_TYPES.VALUE_TEXT,
        colProps: { ...defaultColPropsWithWidth100, searchable: true },
      },
    },
    {
      name: 'Edit',
      apiRef: '__actions__', // not api val
      isUiOnly: true,
      columnDefinition: {
        type: COLUMN_TYPES.ACTION,
        actions: ['Link'],
        colProps: { width: 50, fixedRight: true, align: 'center' },
      },
    },
    {
      name: 'Address',
      apiRef: '__computed__.street_address',
      isUiOnly: true,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      name: 'City',
      apiRef: 'venue_address.municipality',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: { ...defaultColPropsWithWidth100, searchable: true },
      },
    },
    {
      name: 'State',
      apiRef: 'venue_address.country_primary_subdivision',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: statesList,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: { ...defaultColPropsWithWidth100, searchable: true },
      },
    },
    {
      name: 'Zip',
      apiRef: 'venue_address.postal_code',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      name: 'County',
      apiRef: 'venue_address.country_secondary_subdivision',
      searchType: SEARCH_TYPES.SUGGEST_ASYNC,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: { ...defaultColPropsWithWidth100, searchable: true },
      },
    },
    {
      name: 'County FIPS Code',
      apiRef: 'venue_address.county_fips_code',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: { ...defaultColPropsWithWidth100, searchable: true },
      },
    },
    {
      name: 'Trustee',
      apiRef: 'trustee_sale',
      dataType: DATA_TYPES.BOOLEAN,
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: yesNoList,
      columnDefinition: {
        type: COLUMN_TYPES.BOOL,
        colProps: { ...defaultColPropsWithWidth100, searchable: true },
      },
    },
    {
      name: 'Web Live Status',
      apiRef: 'venue_configuration.web_live_status',
      dataType: DATA_TYPES.BOOLEAN,
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: [
        { value: true, text: 'Active' },
        { value: false, text: 'Inactive' },
      ],
      columnDefinition: {
        type: COLUMN_TYPES.STATUS,
        positiveValue: 'Active',
        negativeValue: 'Inactive',
        colProps: { ...defaultColPropsWithWidth100, searchable: true },
      },
    },
    {
      name: 'Created Date',
      apiRef: 'created_date',
      dataType: DATA_TYPES.DATETIME_AS_LOCAL,
      searchType: SEARCH_TYPES.DATE,
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: {
          ...defaultColPropsWithWidth100,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Holding Bucket',
      apiRef: 'venue_configuration.holding_venue',
      dataType: DATA_TYPES.BOOLEAN,
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: yesNoList,
      columnDefinition: {
        type: COLUMN_TYPES.BOOL,
        colProps: {
          ...defaultColPropsWithWidth100,
          sort: COLUMN_SORT_SETTING.NOT_SORTED,
          searchable: true,
        },
      },
    },
    {
      name: 'Test Event',
      apiRef: 'event.event_configuration.test_event',
      dataType: DATA_TYPES.BOOLEAN,
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: yesNoList,
      columnDefinition: {
        type: COLUMN_TYPES.BOOL,
        colProps: { ...defaultColPropsWithWidth100, searchable: true },
      },
    },
    {
      name: 'Venue ID',
      apiRef: 'venue_id',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        dataType: DATA_TYPES.NUMBER,
        colProps: {
          ...defaultColPropsWithWidth100,
          isVisible: false,
          sort: COLUMN_SORT_SETTING.NOT_SORTABLE,
          searchable: true,
        },
      },
    },
  ],
  filterClauses: [],
  sortBy: [
    {
      field: 'venue_name.lowercase',
      order: 'asc',
    },
  ],
}

const linkColumnAction = () => (actionIndex, rowIndex, primaryId) =>
  (
    <Link
      style={{ margin: -8, padding: 8 }}
      key={actionIndex}
      to={`/schedule/venues/${primaryId}/edit`}
      data-elm-id={`action_edit_${rowIndex}`}
    >
      <Icon
        size='large'
        style={{ marginTop: -3 }}
        color='info'
        name='write square'
      />
    </Link>
  )

/**
 *
 * @param {*} fieldDefinitions
 * THIS SELECTOR IS TO MAINTAIN BACKWARDS COMPATIBILITY WITH EVERYTHING IN TOOLKIT
 * It will change/ be removed when everything there changes. This is so we can refactor slowly
 */
export const columnSelector = state =>
  state.fieldDefinitions
    .filter(field => field.columnDefinition)
    .map(col => {
      const column = {
        ...col.columnDefinition,
        ...{ field: col.apiRef, title: col.name },
      }
      if (column.actions) column.actions = column.actions.map(linkColumnAction)
      if (col.dataType) column.dataType = col.dataType
      if (col.searchType) column.searchType = col.searchType
      if (col.options) column.options = col.options
      if (col.formatOptions) column.formatOptions = col.formatOptions
      return column
    })

export const getFilterClauses = state => state.filterClauses

export default reducers.dataTable.dataTable(VENUES, tableConfig)
